import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Column } from 'components/Layout';
import Row from 'components/Layout/Row';
import Avatar from 'further-ui/components/Avatar';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { CommitmentData } from '../../types';

const PaddedRow = styled(Row)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.xxl),
}));

const StyledRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, 0),
  margin: `-${theme.spacing(theme.spacingSizes.xs)} 0`,
}));

type Props = Pick<CommitmentData, 'currencySettings'> & {
  holdingsByHolding: CommitmentData['liveHoldings']['byHolding'];
};

const LiveHoldingsByHolding: React.FC<Props> = ({
  currencySettings,
  holdingsByHolding: { data, totals },
}) => {
  const { currencyLcy } = currencySettings;
  const { toggleRowExpansion, expandedRows } = useExpandedRows();

  const columns: Array<TableColumn<(typeof data)[number]>> = [
    {
      label: 'Asset',
      key: 'assetName',
      paddingY: 0,
      weight: 1.5,
      render: ({ assetName, assetImage, classes }) => (
        <StyledRow centered spacing="xs">
          <Avatar color="random" text={assetName} size="lg" src={assetImage} />
          <Column spacing="xxs">
            <Typography variant="inherit">{assetName}</Typography>
            <Typography variant="caption">
              {classes.length} {pluralize('class', classes.length, 'classes')}
            </Typography>
          </Column>
        </StyledRow>
      ),
      footer: `Total from ${data.length} ${pluralize(
        'holding',
        data.length,
        'holdings',
      )}`,
    },
    {
      label: 'Investment date',
      key: 'investmentDate',
      weight: 1,
      render: ({ investmentDate }) =>
        investmentDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      label: `Invested capital ${currencyLcy}`,
      key: 'investedCapitalLcy',
      weight: 1.25,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.investedCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: `Current hold value ${currencyLcy}`,
      key: 'currentHoldingValueLcy',
      weight: 1.25,
      render: ({ currentHoldingValueLcy }) =>
        numberToCurrencyString(currentHoldingValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Gross MOIC',
      key: 'grossMoic',
      weight: 0.75,
      render: ({ grossMoic }) => `${grossMoic}x`,
      footer: `${totals.grossMoic}x`,
    },
    {
      label: 'Gross IRR',
      key: 'grossIrr',
      weight: 0.75,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
      footer: numberToPercentageString(totals.grossIrr, {
        accuracy: 2,
      }),
    },
    {
      key: 'actions',
      render: (row) => (
        <ButtonBase onClick={() => toggleRowExpansion(row.id)}>
          {expandedRows.has(row.id) ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </ButtonBase>
      ),
    },
  ];

  const subRowColumns: Array<
    TableColumn<(typeof data)[number]['classes'][number]>
  > = [
    {
      key: columns[0].key,
      weight: columns[0].weight,
      render: ({ shareClassName }) => (
        <PaddedRow spacing="xs">{shareClassName}</PaddedRow>
      ),
    },
    {
      key: columns[1].key,
      weight: columns[1].weight,
      render: ({ investmentDate }) =>
        investmentDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      key: columns[2].key,
      weight: columns[2].weight,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[3].key,
      weight: columns[3].weight,
      render: ({ currentHoldingValueLcy }) =>
        numberToCurrencyString(currentHoldingValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[4].key,
      weight: columns[4].weight,
      render: ({ grossMoic }) => `${grossMoic}x`,
    },
    {
      key: columns[5].key,
      weight: columns[5].weight,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
    },
    {
      key: columns[6].key,
      render: () => null,
    },
  ];

  return (
    <Column spacing="md">
      <Table
        isExpandedRow={(row) => expandedRows.has(row.id)}
        getExpandedRows={(row) =>
          row.classes.map((holdingClass, rowIndex) => ({
            key: holdingClass.id,
            columns: subRowColumns.map((col) => ({
              key: col.key,
              content: col.render ? col.render(holdingClass, rowIndex) : null,
            })),
          }))
        }
        emptyMessage="No holdings"
        columns={columns}
        rows={data}
      />
    </Column>
  );
};

export default LiveHoldingsByHolding;
